import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { SpaceProps, WidthProps } from 'styled-system';
import {
  backgroundColor,
  bottom,
  boxShadow,
  gridGap,
  left,
  position,
  space,
  width,
  zIndex,
} from 'styled-system';

import { STICKY_CONTAINER_BOTTOM_Z_INDEX } from '../../constants/z-index';
import { useCloseDrawer } from '../../hooks/drawer';
import { useLocalization } from '../../providers/LocaleProvider';
import { boxSizing } from '../../theme/system-utilities';
import { Button, ButtonLink } from '../Button';
import { Flex } from '../Flex';
import { HeadlineH8 } from '../Typography';

const CHECKOUT_URL = '/checkout/cart';

const UnstyledButtonsContainer = styled(Flex)<any>`
  ${backgroundColor};
  ${boxShadow};
  ${boxSizing};
  ${bottom};
  ${gridGap};
  ${left};
  ${position};
  ${space};
  ${width};
  ${zIndex};
`;

const ButtonsContainer = (props: ComponentProps<typeof UnstyledButtonsContainer>) => (
  <UnstyledButtonsContainer
    backgroundColor="sys.neutral.background.default"
    boxShadow="low"
    boxSizing="border-box"
    bottom={0}
    flexDirection="row"
    gridGap="sp_8"
    left={0}
    position="fixed"
    py="sp_12"
    px="sp_16"
    width={1}
    zIndex={STICKY_CONTAINER_BOTTOM_Z_INDEX}
    {...props}
  />
);

const UnstyledSecondaryButton = styled(Button)<SpaceProps & WidthProps>`
  ${space};
  ${width};
`;

const SecondaryButton = (props: ComponentProps<typeof UnstyledSecondaryButton>) => (
  <UnstyledSecondaryButton kind="secondary-v2" py="sp_12" width={1} {...props} />
);

const UnstyledCtaLink = styled(ButtonLink)`
  ${boxShadow};
  ${space};
  ${width};
`;

const CtaLink = (props: ComponentProps<typeof UnstyledCtaLink>) => (
  <UnstyledCtaLink boxShadow="none" py="sp_12" width={1} {...props} />
);

const CtaButtons = () => {
  const { t, prefix } = useLocalization();
  const continueShoppingClickHandler = useCloseDrawer();

  return (
    <ButtonsContainer>
      <SecondaryButton onClick={continueShoppingClickHandler}>
        <HeadlineH8 as="span" color="inherit">
          {t('h24_continue_shopping')}
        </HeadlineH8>
      </SecondaryButton>
      <CtaLink href={prefix(CHECKOUT_URL)}>
        <HeadlineH8 as="span" color="inherit">
          {t('h24_go_to_cart')}
        </HeadlineH8>
      </CtaLink>
    </ButtonsContainer>
  );
};

export default CtaButtons;
